import { LogoutOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Result, Space } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe, usePublicContact } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { useSiteContext } from '../context/SiteContext';
import { formatDate } from '../format';
import { SimpleShell } from '../layout/SimpleShell';
import { TenantExportCard } from './admin/OrganisationPage';

/**
 * The organisation of the user is suspended by the vendor (Technical Specifications 15.3): the
 * API answers only who they are and, for its administrators, the full export of its data.
 */
export function SuspendedPage() {
  const { t } = useTranslation();
  const { me } = useSiteContext();
  const { signOut } = useAuth();
  const contact = usePublicContact().data;
  const tenant = me?.tenant;
  // the vendor may lift the suspension at any time: the profile is read again on return to the
  // tab and every minute, and the application comes back by itself
  const { refetch } = useMe();
  useEffect(() => {
    const again = () => void refetch();
    const timer = window.setInterval(again, 60_000);
    window.addEventListener('focus', again);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', again);
    };
  }, [refetch]);
  return (
    <SimpleShell context={tenant?.name}>
      <Result
        status="warning"
        title={t('suspended.title')}
        subTitle={t('suspended.description', { date: formatDate(tenant?.suspendedAt) })}
        extra={
          <Space wrap>
            {contact?.support && (
              <Button type="primary" icon={<MailOutlined />} href={`mailto:${contact.support}`}>
                {t('suspended.contact', { email: contact.support })}
              </Button>
            )}
            <Button icon={<LogoutOutlined />} onClick={() => void signOut()}>
              {t('app.signOut')}
            </Button>
          </Space>
        }
      />
      {tenant?.suspensionReason && (
        <Alert
          type="info"
          showIcon
          style={{ maxWidth: 720, margin: '0 auto 16px' }}
          title={t('suspended.reason')}
          description={tenant.suspensionReason}
        />
      )}
      {me?.principal.tenantRoles.includes('TENANT_ADMIN') && (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <TenantExportCard />
        </div>
      )}
    </SimpleShell>
  );
}
